// src/pages/AdminVendorRequirements.jsx
import React, { useEffect, useState } from "react";
import API from "../services/api";
import { useNavigate } from "react-router-dom";
import {
  Spinner,
  Alert,
  Form,
  Card,
  Modal,
} from "react-bootstrap";
import { BsWhatsapp } from "react-icons/bs";
import "./AdminVendorRequirements.css";
import { sendPortalWhatsApp } from "../utils/whatsapp";
import RequirementEditForm from "./RequirementEditForm";

/* ---------------- helpers ---------------- */

const vendorDisplay = (req) => {
  // prefer enriched vendor block from backend
  if (req.vendor) {
    const name = req.vendor.companyName || req.vendor.fullName || "N/A";
    const contact = req.vendor.mobile || req.vendor.email || "";
    return { name, contact };
  }

  // fallbacks for older payloads:
  const company = req.vendorId?.companyName || null;
  const userName =
    req.vendorId?.userId?.fullName || // if vendorId is a Vendor with userId populated
    req.vendorId?.fullName || // if vendorId is a User doc
    null;
  const name = company || userName || "N/A";
  const contact =
    req.vendorId?.mobile ||
    req.vendorId?.userId?.mobile ||
    "";
  return { name, contact };
};

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })
    : "-";

const requirementCode = (req) =>
  req?.requirementId || req?.code || `REQ-${String(req?._id || "").slice(-6).toUpperCase() || "NA"}`;

const statusClass = (value = "") => String(value).toLowerCase().replace(/\s+/g, "-");

const locationTextOnly = (req) => {
  if (req.locationText && req.locationText.trim()) return req.locationText;
  const loc = req.location;
  if (!loc) return "N/A";
  if (typeof loc === "string") return loc.trim() || "N/A";
  const { address, line1, area, city, state, pincode } = loc;
  const text = [address, line1, area, city, state, pincode]
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .join(", ");
  return text || "N/A";
};

const vendorPhone = (req) =>
  req?.vendor?.mobile ||
  req?.vendor?.whatsappNumber ||
  req?.vendorId?.contactMobile ||
  req?.vendorId?.whatsappNumber ||
  req?.vendorId?.userId?.mobile ||
  req?.vendorId?.mobile ||
  "";

const paymentBadge = (payment = {}) => {
  if (payment?.isPaid) {
    return <span className="vr-pill status-completed">Paid</span>;
  }
  const s = String(payment?.latestStatus || "unpaid").toLowerCase();
  if (s === "created" || s === "pending") return <span className="vr-pill approval-pending">Pending</span>;
  if (s === "failed" || s === "error" || s === "cancelled") return <span className="vr-pill approval-rejected">Failed</span>;
  return <span className="vr-pill status-pending">Unpaid</span>;
};

const requirementAmountOf = (req = {}) =>
  Number(req?.requirementAmount ?? req?.payment?.latestAmountRupees ?? 0);

const requirementItemsOf = (req = {}) => {
  if (Array.isArray(req.items) && req.items.length) return req.items;
  return [
    {
      productType: req.productType || "",
      quantity: req.quantity ?? "",
      unit: req.unit || "",
      requirementAmount: req.requirementAmount ?? 0,
    },
  ];
};

const editFlowLabel = (req = {}) => {
  const status = String(req?.editFlow?.status || "none");
  switch (status) {
    case "pending_admin_approval":
      return "Admin Approval Pending";
    case "vendor_rejected":
      return "Vendor Rejected";
    case "admin_rejected":
      return "Admin Rejected";
    case "approved":
      return "Edit Approved";
    default:
      return "No Pending Edit";
  }
};

/* ---------------- component ---------------- */

const AdminVendorRequirements = () => {
  const navigate = useNavigate();
  const [requirements, setRequirements] = useState([]);
  const [products, setProducts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedReq, setSelectedReq] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    draft: {
      productType: "",
      quantity: "",
      unit: "KG",
      requirementAmount: "",
    },
    items: [],
    locationText: "",
    notes: "",
  });

  // fetch all requirements (admin) — expects BE to enrich with vendor + locationText
  const fetchRequirements = async () => {
    try {
      const res = await API.get("/admin/vendor-requirements");
      const list = Array.isArray(res.data) ? res.data : [];
      setRequirements(list);
      setError("");
      return list;
    } catch (err) {
      console.error("Error fetching requirements:", err);
      setError("Failed to load vendor requirements");
      return [];
    } finally {
      setLoading(false);
    }
  };

  // fetch employees for assignment
  const fetchEmployees = async () => {
    try {
      const res = await API.get("/admin/employees");
      setEmployees(res.data || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await API.get("/admin/products");
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching products:", err);
      setProducts([]);
    }
  };

  useEffect(() => {
    fetchRequirements();
    fetchEmployees();
    fetchProducts();
    const interval = setInterval(fetchRequirements, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleApprove = async (id) => {
    try {
      await API.patch(`/admin/vendor-requirements/${id}/approve`);
      fetchRequirements();
    } catch (err) {
      console.error("Error approving requirement:", err);
    }
  };

  const handleReject = async (id) => {
    try {
      await API.patch(`/admin/vendor-requirements/${id}/reject`);
      fetchRequirements();
    } catch (err) {
      console.error("Error rejecting requirement:", err);
    }
  };

  const handleAssign = async (id, value) => {
    try {
      // "none" => unassign
      const payload = { employeeId: value && value !== "none" ? value : null };
      await API.patch(`/admin/vendor-requirements/${id}/assign`, payload);
      fetchRequirements();
    } catch (err) {
      console.error("Error assigning employee:", err);
    }
  };

  const openDetails = (req) => {
    setSelectedReq(req);
    setEditForm({
      draft: {
        productType: "",
        quantity: "",
        unit: "KG",
        requirementAmount: "",
      },
          items: requirementItemsOf(req).map((it) => ({
            productId: it.productId || "",
            productType: it.productType || "",
            packLabel: it.packLabel || "",
            quantity: it.quantity ?? "",
            unit: it.unit || "KG",
            rate: Number(it.rate || 0),
            subtotalAmount: Number(it.subtotalAmount || 0),
            discountPercent: Number(it.discountPercent || 0),
            discountAmount: Number(it.discountAmount || 0),
            gstPercent: Number(it.gstPercent || 0),
            gstAmount: Number(it.gstAmount || 0),
            totalAmount: Number(it.totalAmount || it.requirementAmount || 0),
            requirementAmount: Number(it.requirementAmount || it.totalAmount || 0),
          })),
      locationText: locationTextOnly(req) || "",
      notes: req?.notes || "",
    });
  };

  const saveRequirementEdits = async (overrideForm = editForm) => {
    if (!selectedReq?._id) return;
    if (!Array.isArray(overrideForm.items) || !overrideForm.items.length) {
      setError("At least one product item is required");
      return;
    }
    if (!String(overrideForm.locationText || "").trim()) {
      setError("Location is required");
      return;
    }

    try {
      setEditSaving(true);
      setError("");
      await API.patch(`/admin/vendor-requirements/${selectedReq._id}`, {
        items: overrideForm.items,
        notes: overrideForm.notes,
        locationText: String(overrideForm.locationText || "").trim(),
      });
      const latest = await fetchRequirements();
      const refreshed = latest.find((x) => String(x._id) === String(selectedReq._id));
      if (refreshed) {
        setSelectedReq(refreshed);
        setEditForm({
          draft: {
            productType: "",
            quantity: "",
            unit: "KG",
            requirementAmount: "",
          },
          items: requirementItemsOf(refreshed).map((it) => ({
            productId: it.productId || "",
            productType: it.productType || "",
            packLabel: it.packLabel || "",
            quantity: it.quantity ?? "",
            unit: it.unit || "KG",
            rate: Number(it.rate || 0),
            subtotalAmount: Number(it.subtotalAmount || 0),
            discountPercent: Number(it.discountPercent || 0),
            discountAmount: Number(it.discountAmount || 0),
            gstPercent: Number(it.gstPercent || 0),
            gstAmount: Number(it.gstAmount || 0),
            totalAmount: Number(it.totalAmount || it.requirementAmount || 0),
            requirementAmount: Number(it.requirementAmount || it.totalAmount || 0),
          })),
          locationText: locationTextOnly(refreshed) || "",
          notes: refreshed?.notes || "",
        });
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update requirement");
    } finally {
      setEditSaving(false);
    }
  };

  const sendWhatsAppToVendor = async (req) => {
    const phone = vendorPhone(req);
    if (!phone) {
      setError("Vendor WhatsApp/mobile number not available");
      return;
    }
    try {
      await sendPortalWhatsApp({
        to: phone,
        recipientName: vendorDisplay(req).name,
        message: `Hello ${vendorDisplay(req).name}, requirement ${requirementCode(req)} for ${req.productType || "product"} (${req.quantity || "-"} ${req.unit || ""}) is currently ${req.approvalStatus || req.status || "pending"}.`,
        sourceModule: "vendor_requirements",
        eventType: "manual_requirement_message",
        entityType: "Requirement",
        entityId: req?._id,
        isAdmin: true,
      });
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to send WhatsApp");
    }
  };

  return (
    <div className="vr-page">
      <div className="vr-container">
        <div className="vr-topbar">
          <div className="vr-title-wrap">
            <div className="vr-icon">
              <span className="material-symbols-outlined">assignment</span>
            </div>
            <div>
              <div className="vr-title">Vendor Requirements</div>
              <div className="vr-subtitle">Review and manage pending product requests</div>
            </div>
          </div>
          <div className="vr-actions">
            <button className="vr-ghost-btn" onClick={fetchRequirements}>
              <span className="material-symbols-outlined">refresh</span>
              Refresh
            </button>
            <button className="vr-ghost-btn" onClick={() => navigate("/admin/requirement-dispatches")}>
              <span className="material-symbols-outlined">local_shipping</span>
              Dispatch Board
            </button>
            <button className="vr-primary-btn" onClick={fetchRequirements}>
              <span className="material-symbols-outlined">add</span>
              Manual Entry
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="success" />
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : requirements.length === 0 ? (
          <Alert variant="info">No vendor requirements available.</Alert>
        ) : (
          <div className="vr-cards-grid">
            {requirements.map((req) => {
              const v = vendorDisplay(req);
              const items = requirementItemsOf(req);
              return (
                <Card key={req._id} className="vr-req-card">
                  <Card.Body className="vr-card-body">
                    <div className="vr-card-head">
                      <div>
                        <div className="vr-vendor-name">{v.name}</div>
                        <div className="vr-vendor-contact">{v.contact || "No contact available"}</div>
                      </div>
                      <div className="vr-pill-row">
                        <span className={`vr-pill approval-${String(req.approvalStatus || "").toLowerCase()}`}>
                          {req.approvalStatus || "-"}
                        </span>
                      </div>
                    </div>

                    <div className="vr-card-section">
                      <div className="vr-section-label">Products</div>
                      <div className="vr-items-list">
                        {items.map((it, idx) => (
                          <div key={`${req._id}_${idx}`} className="vr-item-line">
                            <span className="vr-item-name">{it.packLabel || it.productType || "-"}</span>
                            <span>{it.quantity ?? "-"} {it.unit || ""}</span>
                            <span>₹ {Number(it.requirementAmount || it.totalAmount || 0).toLocaleString("en-IN")}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="vr-info-grid">
                      <div className="vr-info-box">
                        <div className="vr-section-label">Amount</div>
                        <div className="vr-info-value">
                          {requirementAmountOf(req) > 0
                            ? `₹ ${requirementAmountOf(req).toLocaleString("en-IN")}`
                            : "-"}
                        </div>
                      </div>
                      <div className="vr-info-box">
                        <div className="vr-section-label">Assigned</div>
                        <div className="vr-info-value">{req.assignedEmployee?.fullName || "Not Assigned"}</div>
                      </div>
                      <div className="vr-info-box vr-info-box-full">
                        <div className="vr-section-label">Location</div>
                        <div className="vr-info-value">{locationTextOnly(req)}</div>
                      </div>
                      <div className="vr-info-box vr-info-box-full">
                        <div className="vr-section-label">Notes</div>
                        <div className="vr-info-value">{req.notes || "-"}</div>
                      </div>
                      <div className="vr-info-box">
                        <div className="vr-section-label">Date</div>
                        <div className="vr-info-value">{fmtDate(req.createdAt)}</div>
                      </div>
                      <div className="vr-info-box">
                        <div className="vr-section-label">Payment</div>
                        <div className="vr-info-value">
                          {req.payment?.latestAmountRupees
                            ? `₹ ${Number(req.payment.latestAmountRupees).toLocaleString("en-IN")}`
                            : "Unpaid"}
                        </div>
                      </div>
                    </div>

                    <div className="vr-tag-row">
                      <span className={`vr-pill status-${String(req.status || "").toLowerCase()}`}>
                        {req.status || "-"}
                      </span>
                      <span className={`vr-pill status-${statusClass(req.editFlow?.status || "none")}`}>
                        {editFlowLabel(req)}
                      </span>
                      {paymentBadge(req.payment)}
                    </div>

                    {req.approvalStatus === "approved" && (
                      <Form.Select
                        size="sm"
                        value={req.assignedEmployee?._id || "none"}
                        onChange={(e) =>
                          handleAssign(req._id, e.target.value)
                        }
                        className="mb-2"
                      >
                        <option value="none">— None — (Unassign)</option>
                        {employees.map((emp) => (
                          <option key={emp._id} value={emp._id}>
                            {emp.fullName}
                          </option>
                        ))}
                      </Form.Select>
                    )}

                    <div className="vr-actions-wrap">
                      <button className="vr-action-btn" onClick={() => openDetails(req)}>
                        View Details
                      </button>
                      <button className="vr-action-btn" onClick={() => sendWhatsAppToVendor(req)}>
                        <span className="d-inline-flex align-items-center gap-1">
                          <BsWhatsapp />
                          <span>WhatsApp</span>
                        </span>
                      </button>
                      {(req.approvalStatus === "pending" ||
                        req.approvalStatus === "rejected" ||
                        req.editFlow?.status === "pending_admin_approval") && (
                        <button className="vr-action-btn vr-action-success" onClick={() => handleApprove(req._id)}>
                          {req.editFlow?.status === "pending_admin_approval" ? "Approve Edit" : "Approve"}
                        </button>
                      )}
                      {(req.approvalStatus !== "rejected" || req.editFlow?.status === "pending_admin_approval") && (
                        <button className="vr-action-btn vr-action-danger" onClick={() => handleReject(req._id)}>
                          {req.editFlow?.status === "pending_admin_approval" ? "Reject Edit" : "Reject"}
                        </button>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        show={!!selectedReq}
        onHide={() => setSelectedReq(null)}
        centered
        size="lg"
        dialogClassName="vrm-modal"
      >
        <Modal.Header className="vrm-head">
          <div>
            <h5 className="mb-0 vrm-title">Requirement Details</h5>
            <p className="mb-0 vrm-sub">ID: #{selectedReq ? requirementCode(selectedReq) : "—"}</p>
          </div>
          <button className="vrm-close-btn" onClick={() => setSelectedReq(null)}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </Modal.Header>
        <Modal.Body className="vrm-body">
          {selectedReq && (
            <div className="vrm-wrap">
              <div className="vrm-grid">
                <div className="vrm-item">
                  <label>Vendor</label>
                  <div className="vrm-vendor">{vendorDisplay(selectedReq).name}</div>
                  <div className="vrm-muted">{vendorDisplay(selectedReq).contact || "—"}</div>
                </div>
                <div className="vrm-item">
                  <label>Product</label>
                  <div className="vrm-product">
                    <span className="material-symbols-outlined">inventory_2</span>
                    <span>{selectedReq.productType || "-"}</span>
                  </div>
                  <div className="vr-items-list mt-2">
                    {requirementItemsOf(selectedReq).map((it, idx) => (
                      <div key={`modal_${selectedReq._id}_${idx}`} className="vr-item-line">
                        {it.packLabel || it.productType || "-"} | {it.quantity ?? "-"} {it.unit || ""} | ₹ {Number(it.requirementAmount || it.totalAmount || 0).toLocaleString("en-IN")}
                        {Number(it.gstPercent || 0) > 0 ? ` | GST ${Number(it.gstPercent).toLocaleString("en-IN")} %` : ""}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="vrm-item">
                  <label>Quantity</label>
                  <div className="vrm-strong">
                    {selectedReq.quantity ?? "-"} <span>{selectedReq.unit || ""}</span>
                  </div>
                </div>
                <div className="vrm-item">
                  <label>Requirement Amount</label>
                  <div className="vrm-strong">
                    {requirementAmountOf(selectedReq) > 0
                      ? `₹ ${requirementAmountOf(selectedReq).toLocaleString("en-IN")}`
                      : "-"}
                  </div>
                  {Number(selectedReq.gstAmount || 0) > 0 ? (
                    <div className="vrm-muted">
                      Base ₹ {Number(selectedReq.subtotalAmount || 0).toLocaleString("en-IN")} + GST ₹ {Number(selectedReq.gstAmount || 0).toLocaleString("en-IN")}
                    </div>
                  ) : null}
                </div>
                <div className="vrm-item">
                  <label>Location</label>
                  <div className="vrm-strong">
                    <span className="material-symbols-outlined">location_on</span>
                    {locationTextOnly(selectedReq)}
                  </div>
                </div>
                <div className="vrm-item">
                  <label>Workflow & Approval</label>
                  <div className="vrm-chip-row">
                    <span className={`vrm-chip status-${statusClass(selectedReq.status)}`}>
                      {selectedReq.status || "-"}
                    </span>
                    <span className={`vrm-chip approval-${statusClass(selectedReq.approvalStatus)}`}>
                      {selectedReq.approvalStatus || "-"}
                    </span>
                    <span className={`vrm-chip status-${statusClass(selectedReq.editFlow?.status || "none")}`}>
                      {editFlowLabel(selectedReq)}
                    </span>
                  </div>
                </div>
                <div className="vrm-item">
                  <label>Payment Status</label>
                  <div className="vrm-chip-row">
                    {paymentBadge(selectedReq.payment)}
                    {selectedReq.payment?.latestAmountRupees ? (
                      <span className="vrm-chip">₹ {Number(selectedReq.payment.latestAmountRupees).toLocaleString("en-IN")}</span>
                    ) : null}
                  </div>
                </div>
                <div className="vrm-item vrm-split">
                  <div>
                    <label>Assigned To</label>
                    <div className="vrm-strong">{selectedReq.assignedEmployee?.fullName || "Not Assigned"}</div>
                  </div>
                  <div>
                    <label>Date Created</label>
                    <div className="vrm-strong">{fmtDate(selectedReq.createdAt)}</div>
                  </div>
                </div>
              </div>

              <div className="vrm-notes-wrap">
                <label>Additional Notes</label>
                <div className="vrm-notes">{selectedReq.notes || "No additional notes."}</div>
              </div>

              <div className="vrm-notes-wrap">
                <label>Edit Requirement</label>
                <RequirementEditForm
                  products={products}
                  form={editForm}
                  setForm={setEditForm}
                  saving={editSaving}
                  onSubmit={saveRequirementEdits}
                  submitLabel="Save Changes"
                />
              </div>

              <div className="vrm-footer">
                <button
                  className="vrm-btn vrm-btn-ghost"
                  onClick={() => selectedReq.approvalStatus !== "rejected" && handleReject(selectedReq._id)}
                >
                  Cancel Requirement
                </button>
                <button className="vrm-btn vrm-btn-primary" onClick={() => setSelectedReq(null)}>
                  <span className="material-symbols-outlined">close</span>
                  Close
                </button>
                <button
                  className="vrm-btn vrm-btn-dark"
                  onClick={() => handleApprove(selectedReq._id)}
                  disabled={selectedReq.approvalStatus === "approved"}
                >
                  <span className="material-symbols-outlined">check_circle</span>
                  Approve Now
                </button>
                <button
                  className="vrm-btn vrm-btn-primary"
                  onClick={() => sendWhatsAppToVendor(selectedReq)}
                >
                  <BsWhatsapp />
                  <span>WhatsApp</span>
                </button>
              </div>
              <div className="vrm-gradient" />
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminVendorRequirements;
